import React, { useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Button, Card, Col, Dropdown, Form, Row } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import classNames from 'classnames';
import { getSize } from 'helpers/utils';
import Flex from 'components/common/Flex';
import CardDropdown from 'components/common/CardDropdown';
import cloudUpload from 'assets/img/icons/cloud-upload.svg';
import { toast } from 'react-toastify';
import csvtojson from 'csvtojson';
import * as XLSX from 'xlsx';
import { useAuth } from 'nursd-dashboard/context/AuthContext';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import AdvanceTableFooter from 'components/common/advance-table/AdvanceTableFooter';
import useAdvanceTable from 'hooks/useAdvanceTable';
import moment from 'moment';
import { getCustomerTableList } from 'nursd-dashboard/api/customerTable';
import { getAllFacilityOrgList } from 'nursd-dashboard/api/organizationTable';
import Select from 'react-select';
import makeAnimated from 'react-select/animated';
import { postJobTable } from 'nursd-dashboard/api/jobTable';
import { useNavigate } from 'react-router-dom';
import IconButton from 'components/common/IconButton';

function ScheduleUpload() {
  const navigate = useNavigate();
  const { authToken, location, organization } = useAuth();
  const {
    handleSubmit,
    setValue,
    formState: { errors },
    clearErrors
  } = useForm();
  const [files, setFiles] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [focus, setFocus] = useState('');

  const [emptyFields, setEmptyFields] = useState('');
  const [validateTable, setValidateTable] = useState(false);
  const [loading, setLoading] = useState(false);
  const [managers, setManagers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [valueManager, setValueManager] = useState(null);
  const [valueCustomer, setValueCustomer] = useState(null);
  const [type, setType] = useState({
    value: 'Shift',
    label: 'Shift'
  });

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
        '.xlsx'
      ]
    },
    onDrop: acceptedFiles => {
      if (errors.uploadedFiles) {
        clearErrors('uploadedFiles');
      }
      setValue('uploadedFiles', acceptedFiles);
      setFiles([
        ...files,
        ...acceptedFiles.map(file =>
          Object.assign(file, {
            preview: URL.createObjectURL(file)
          })
        )
      ]);
    }
  });

  const handleRemove = path => {
    const filteredFiles = files.filter(file => file.path !== path);
    setFiles(filteredFiles);
    setValue('uploadedFiles', filteredFiles.length ? filteredFiles : null);
    setTableData([]);
    setValidateTable(false);
    setEmptyFields('');
  };

  const onSubmit = () => {
    if (files.length === 0) {
      toast.warning('Upload the file to submit', {
        theme: 'colored'
      });
    } else {
      const file = files[0];
      const reader = new FileReader();
      reader.onload = async event => {
        const data = event.target.result;
        let parsedData = [];

        if (file.name.endsWith('.csv')) {
          parsedData = await csvtojson().fromString(data);
        } else if (file.name.endsWith('.xls') || file.name.endsWith('.xlsx')) {
          const workbook = XLSX.read(data, { type: 'binary' });
          const sheetName = workbook.SheetNames[0];
          const sheet = workbook.Sheets[sheetName];
          parsedData = XLSX.utils.sheet_to_json(sheet);
        }

        const column = Object.keys(parsedData[0]).map(key => {
          return { Header: key, accessor: key };
        });

        const headerSpellFixArray = parsedData?.map(item =>
          column?.reduce((acc, curr) => {
            const accessor = curr.accessor.replace(/\s+/g, '_').toLowerCase();
            acc[accessor] = item[curr.accessor] || '';
            return acc;
          }, {})
        );

        // console.log('headerSpellFixArray', headerSpellFixArray);

        setTableData(headerSpellFixArray);

        toast.info('Check the fields before publish.', {
          theme: 'colored'
        });
      };
      reader.readAsBinaryString(file);
    }
  };

  const getFacility = async () => {
    try {
      const cusList = await getAllFacilityOrgList(
        authToken,
        location,
        organization
      );

      const list = cusList.sort((a, b) =>
        a.firstName.toLowerCase().localeCompare(b.firstName.toLowerCase())
      );
      let array = [];
      for (let i = 0; i < list.length; i++) {
        const item = list[i];
        let obj = {
          id: item.id,
          value: item.id,
          uniqueId: item.uniqueId,
          name: item.firstName + ' ' + item.lastName,
          label: item.firstName + ' ' + item.lastName,
          profileImage: item.profileImage,
          emailId: item.emailId,
          facilityLoginControl: item?.facilityLoginControl
        };
        array.push(obj);
      }

      if (list.length === array.length) {
        setManagers(array);
      }
    } catch (error) {
      setCustomers([]);
      console.log(error);
    }
  };

  const getCustomer = async () => {
    try {
      const cusList = await getCustomerTableList(
        authToken,
        location,
        organization
      );

      const list = cusList.sort((a, b) =>
        a.firstName.toLowerCase().localeCompare(b.firstName.toLowerCase())
      );
      let array = [];
      for (let i = 0; i < list.length; i++) {
        const item = list[i];
        let obj = {
          value: item.id,
          name: item.firstName + ' ' + item.lastName,
          label: item.firstName + ' ' + item.lastName,
          avatar: {
            name: item.firstName + ' ' + item.lastName
          },
          email: item.email,
          address: item.fullAddress,
          latitude: item.latitude,
          longitude: item.longitude,
          phone: item.phoneOne,
          status: item?.active,
          joined: item.createdAt,
          original: item
        };
        array.push(obj);
      }

      if (list.length === array.length) {
        setCustomers(array);
      }
    } catch (error) {
      console.log(error);
      setCustomers([]);
    }
  };

  useEffect(() => {
    if (location !== '') {
      getFacility();
      getCustomer();
    }
  }, [authToken, location]);

  const field = [
    'LicenseType',
    'Title',
    'StartDate',
    'EndDate',
    'StartTime',
    'EndTime',
    'Break',
    'Notes',
    'CustomerVisibility'
  ];

  const handleInputChange = (event, index) => {
    const { name, value } = event.target;
    const newData = [...tableData];
    newData[index][name] = value;
    setTableData(newData);
  };

  const validateFields = () => {
    let isValid = true;
    const newData = [...tableData];
    const invalidRows = [];
    const emptyFields = [];

    for (let i = 0; i < newData.length; i++) {
      const data = newData[i];
      const title = data?.title?.toString();
      const jobtype = data?.jobtype?.toString();
      const startdate = data?.startdate?.toString();
      const enddate = data?.enddate?.toString();
      const starttime = data?.starttime?.toString();
      const endtime = data?.endtime?.toString();
      const Break = data?.break?.toString();
      const customervisibility = data?.customervisibility?.toString();

      if (
        title === '' ||
        jobtype === '' ||
        startdate === '' ||
        enddate === '' ||
        starttime === '' ||
        endtime === '' ||
        Break === '' ||
        customervisibility === ''
      ) {
        isValid = false;
        invalidRows.push(data);
      }
    }

    for (let i = 0; i < newData.length; i++) {
      const title = newData[i]['title'];
      const jobtype = newData[i]['jobtype'];
      const startdate = newData[i]['startdate'];
      const enddate = newData[i]['enddate'];
      const starttime = newData[i]['starttime'];
      const endtime = newData[i]['endtime'];
      const Break = newData[i]['break'];
      const customervisibility = newData[i]['customervisibility'];
      if (
        title === '' ||
        jobtype === '' ||
        startdate === '' ||
        enddate === '' ||
        starttime === '' ||
        endtime === '' ||
        Break === '' ||
        customervisibility === ''
      ) {
        isValid = false;
        emptyFields.push(
          `title_${i}`,
          `jobtype_${i}`,
          `startdate_${i}`,
          `enddate_${i}`,
          `starttime_${i}`,
          `endtime_${i}`,
          `break_${i}`,
          `customervisibility_${i}`
        );
      }
    }

    setEmptyFields(emptyFields);

    if (emptyFields.length === 0) {
      setValidateTable(true);
    } else {
      setValidateTable(false);
    }

    return { isValid, invalidRows };
  };

  const columns = [
    {
      accessorKey: 'licensetype',
      header: 'License Types',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: ({ row: { original, index } }) => {
        const { licensetype } = original;
        return (
          <input
            required
            style={
              emptyFields.includes(`licensetype_${index}`)
                ? {
                    border: '1px solid red',
                    padding: '5px',
                    outline: '1px solid red',
                    borderRadius: '5px'
                  }
                : {
                    border: '1px solid #ccc',
                    padding: '5px',
                    outline: '1px solid #ccc',
                    borderRadius: '5px'
                  }
            }
            autoFocus={focus === `licensetype_${index}`}
            type="text"
            name="licensetype"
            value={licensetype}
            onChange={event => {
              setFocus(`licensetype_${index}`);
              handleInputChange(event, index);
            }}
          />
        );
      }
    },
    {
      accessorKey: 'title',
      header: 'Title',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: ({ row: { original, index } }) => {
        const { title } = original;
        return (
          <input
            required
            style={
              emptyFields.includes(`title_${index}`)
                ? {
                    border: '1px solid red',
                    padding: '5px',
                    outline: '1px solid red',
                    borderRadius: '5px'
                  }
                : {
                    border: '1px solid #ccc',
                    padding: '5px',
                    outline: '1px solid #ccc',
                    borderRadius: '5px'
                  }
            }
            autoFocus={focus === `title_${index}`}
            type="text"
            name="title"
            value={title}
            onChange={event => {
              setFocus(`title_${index}`);
              handleInputChange(event, index);
            }}
          />
        );
      }
    },
    {
      accessorKey: 'startdate',
      header: 'StartDate (MM-DD-YYYY)',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: ({ row: { original, index } }) => {
        const { startdate } = original;
        return (
          <input
            required
            style={
              emptyFields.includes(`startdate_${index}`)
                ? {
                    border: '1px solid red',
                    padding: '5px',
                    outline: '1px solid red',
                    borderRadius: '5px'
                  }
                : {
                    border: '1px solid #ccc',
                    padding: '5px',
                    outline: '1px solid #ccc',
                    borderRadius: '5px'
                  }
            }
            autoFocus={focus === `startdate_${index}`}
            type="text"
            name="startdate"
            value={startdate}
            onChange={event => {
              setFocus(`startdate_${index}`);
              handleInputChange(event, index);
            }}
          />
        );
      }
    },
    {
      accessorKey: 'enddate',
      header: 'EndDate (MM-DD-YYYY)',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: ({ row: { original, index } }) => {
        const { enddate } = original;
        return (
          <input
            required
            style={
              emptyFields.includes(`enddate_${index}`)
                ? {
                    border: '1px solid red',
                    padding: '5px',
                    outline: '1px solid red',
                    borderRadius: '5px'
                  }
                : {
                    border: '1px solid #ccc',
                    padding: '5px',
                    outline: '1px solid #ccc',
                    borderRadius: '5px'
                  }
            }
            autoFocus={focus === `enddate_${index}`}
            type="text"
            name="enddate"
            value={enddate}
            onChange={event => {
              setFocus(`enddate_${index}`);
              handleInputChange(event, index);
            }}
          />
        );
      }
    },
    {
      accessorKey: 'starttime',
      header: 'StartTime',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: ({ row: { original, index } }) => {
        const { starttime } = original;
        return (
          <input
            required
            style={
              emptyFields.includes(`starttime_${index}`)
                ? {
                    border: '1px solid red',
                    padding: '5px',
                    outline: '1px solid red',
                    borderRadius: '5px'
                  }
                : {
                    border: '1px solid #ccc',
                    padding: '5px',
                    outline: '1px solid #ccc',
                    borderRadius: '5px'
                  }
            }
            autoFocus={focus === `starttime_${index}`}
            type="text"
            name="starttime"
            value={starttime}
            onChange={event => {
              setFocus(`starttime_${index}`);
              handleInputChange(event, index);
            }}
          />
        );
      }
    },
    {
      accessorKey: 'endtime',
      header: 'EndTime',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: ({ row: { original, index } }) => {
        const { endtime } = original;
        return (
          <input
            required
            style={
              emptyFields.includes(`endtime_${index}`)
                ? {
                    border: '1px solid red',
                    padding: '5px',
                    outline: '1px solid red',
                    borderRadius: '5px'
                  }
                : {
                    border: '1px solid #ccc',
                    padding: '5px',
                    outline: '1px solid #ccc',
                    borderRadius: '5px'
                  }
            }
            autoFocus={focus === `endtime_${index}`}
            type="text"
            name="endtime"
            value={endtime}
            onChange={event => {
              setFocus(`endtime_${index}`);
              handleInputChange(event, index);
            }}
          />
        );
      }
    },
    {
      accessorKey: 'customervisibility',
      header: 'CustomerVisibility',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: ({ row: { original, index } }) => {
        const { customervisibility } = original;
        return (
          <input
            required
            style={
              emptyFields.includes(`customervisibility_${index}`)
                ? {
                    border: '1px solid red',
                    padding: '5px',
                    outline: '1px solid red',
                    borderRadius: '5px'
                  }
                : {
                    border: '1px solid #ccc',
                    padding: '5px',
                    outline: '1px solid #ccc',
                    borderRadius: '5px'
                  }
            }
            autoFocus={focus === `customervisibility_${index}`}
            type="text"
            name="customervisibility"
            value={customervisibility}
            onChange={event => {
              setFocus(`customervisibility_${index}`);
              handleInputChange(event, index);
            }}
          />
        );
      }
    },
    {
      accessorKey: 'notes',
      header: 'Notes',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: ({ row: { original, index } }) => {
        const { notes } = original;
        return (
          <input
            required
            style={
              emptyFields.includes(`notes_${index}`)
                ? {
                    border: '1px solid red',
                    padding: '5px',
                    outline: '1px solid red',
                    borderRadius: '5px'
                  }
                : {
                    border: '1px solid #ccc',
                    padding: '5px',
                    outline: '1px solid #ccc',
                    borderRadius: '5px'
                  }
            }
            autoFocus={focus === `notes_${index}`}
            type="text"
            name="notes"
            value={notes}
            onChange={event => {
              setFocus(`notes_${index}`);
              handleInputChange(event, index);
            }}
          />
        );
      }
    }
  ];

  const table = useAdvanceTable({
    data: tableData,
    columns,
    selection: true,
    sortable: true,
    pagination: true,
    perPage: 10
  });

  function timeDifferent(sTime, eTime) {
    const startTime = moment(
      moment(new Date(sTime)).format('hh:mma'),
      'hh:mm A'
    );

    const start = moment(new Date(sTime));
    const end = moment(new Date(eTime));

    const endTime = moment(moment(new Date(eTime)).format('hh:mma'), 'hh:mm A');
    const endTimeEnd = moment(
      moment(new Date(eTime)).format('hh:mma'),
      'hh:mm A'
    ).add(1, 'day');

    const duration = moment.duration(endTime.diff(startTime));
    const durationEnd = moment.duration(endTimeEnd.diff(startTime));

    const hours = Math.floor(duration.asHours());
    const hoursEnd = Math.floor(durationEnd.asHours());
    const minutes = Math.floor(duration.asMinutes()) - hours * 60;

    // const onlyHours = `${
    //   end.hour() <= 12 && start.hour() <= 12
    //     ? hours
    //     : end.hour() <= 12
    //     ? hoursEnd
    //     : hours
    // } ${
    //   end.hour() <= 12 && start.hour() <= 12
    //     ? hours <= 1
    //       ? "hour"
    //       : "hours"
    //     : end.hour() <= 12
    //     ? hoursEnd <= 1
    //       ? "hour"
    //       : "hours"
    //     : hours <= 1
    //     ? "hour"
    //     : "hours"
    // }`;

    const onlyHours = `${
      end.hour() <= 12 && start.hour() <= 12
        ? hours
        : end.hour() <= 12
        ? hoursEnd
        : hours
    }${hours <= 1 ? 'hour' : 'hours'} ${
      minutes <= 0 ? '' : `${minutes}minutes`
    }`;

    return onlyHours;
  }

  function GetDateMergedTime(date, time) {
    // console.log(date, time);

    // Assuming date is in the format DD-MM-YYYY
    const date1 = moment(date, 'DD-MM-YYYY').format('DD-MM-YYYY');

    const time1 = moment(time, 'hh:mm A').format('hh:mm A');

    // console.log(date1, time1)

    const mergedTime = moment(`${date1} ${time1}`, 'DD-MM-YYYY hh:mm A');

    // console.log(mergedTime.format('DD-MM-YYYY hh:mm A'));

    return mergedTime;
  }

  const postJob = async (
    licenseType,
    shiftTitle,
    startDate,
    endDate,
    startTime,
    endTime,
    jBreak,
    selectCustomer,
    unit,
    floor,
    fullAddress,
    latitude,
    longitude,
    notes,
    jobType,
    jobTiming,
    customerVisibility,
    customerID,
    timeStamp
  ) => {
    try {
      const item = await postJobTable(
        authToken,
        licenseType,
        shiftTitle,
        '',
        [],
        '',
        '0',
        moment(startDate).format('YYYY-MM-DD'),
        moment(endDate).format('YYYY-MM-DD'),
        startTime.toString(),
        endTime.toString(),
        jBreak,
        customerID === null ? '' : selectCustomer,
        customerID === null ? '' : customerID?.toString(),
        unit,
        floor,
        fullAddress,
        latitude.toString(),
        longitude.toString(),
        '1',
        false,
        notes,
        jobType,
        'Open',
        '',
        '',
        [],
        false,
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        timeDifferent(startTime, endTime),
        jobTiming,
        '',
        [],
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        false,
        false,
        false,
        false,
        false,
        customerVisibility,
        '',
        '',
        {},
        '',
        valueManager?.value.toString(),
        false,
        '',
        '',
        '',
        false,
        false,
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        timeStamp,
        '',
        '',
        '',
        '',
        organization,
        location
      );
      return item;
    } catch (error) {
      console.log('data save error:', error);
    }
  };

  const deepEqual = (obj1, obj2) => {
    if (obj1 === obj2) {
      return true;
    }

    if (
      typeof obj1 !== 'object' ||
      obj1 === null ||
      typeof obj2 !== 'object' ||
      obj2 === null
    ) {
      return false;
    }

    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    if (keys1.length !== keys2.length) {
      return false;
    }

    for (const key of keys1) {
      // eslint-disable-next-line no-prototype-builtins
      if (!obj2?.hasOwnProperty(key)) {
        return false;
      }

      if (!deepEqual(obj1[key], obj2[key])) {
        return false;
      }
    }

    return true;
  };

  const upload = () => {
    const { isValid, invalidRows } = validateFields();

    const validRows = tableData.filter(data => {
      // Check if the row object is not present in the invalidRows array
      return !invalidRows.some(invalidRow => deepEqual(invalidRow, data));
    });

    if (isValid) {
      setLoading(true);
      // console.log('validRows', validRows);
      const postJobPromise = data => {
        // eslint-disable-next-line no-async-promise-executor
        return new Promise(async (resolve, reject) => {
          // console.log(data)

          const licenseTypeArray = data?.licensetype
            ?.split(',')
            .map(type => type.trim());

          const ct = new Date(
            GetDateMergedTime(data?.startdate, data?.starttime).toString()
          );
          const currentHour = ct.getHours();
          let jobTiming =
            currentHour >= 5 && currentHour < 12
              ? 'Morning'
              : currentHour >= 12 && currentHour < 17
              ? 'Afternoon'
              : currentHour >= 17 && currentHour < 20
              ? 'Evening'
              : 'Night';

          postJob(
            licenseTypeArray,
            data.title,
            moment(data.startdate).format('YYYY-MM-DD'),
            moment(data.enddate).format('YYYY-MM-DD'),
            GetDateMergedTime(
              moment(data.startdate).format('DD-MM-YYYY'),
              data.starttime
            ).toString(),
            GetDateMergedTime(
              moment(data.enddate).format('DD-MM-YYYY'),
              data.endtime
            ).toString(),
            'No Break',
            valueCustomer?.name?.toString(),
            data.unit,
            data.floor,
            valueCustomer.address,
            valueCustomer.latitude.toString(),
            valueCustomer.longitude.toString(),
            data?.notes,
            type.value,
            jobTiming,
            data.customervisibility?.toLowerCase() === 'yes' ? true : false,
            valueCustomer?.value?.toString(),
            GetDateMergedTime(
              moment(data.startdate).format('DD-MM-YYYY'),
              data.starttime
            )
              .valueOf()
              .toString()
          )
            .then(res => {
              // console.log(res);
              resolve(res); // Resolve the promise with the response
            })
            .catch(error => {
              console.error('error', error);
              reject(error); // Reject the promise with the error
            });
        });
      };

      // console.log(validRows?.length);
      Promise.all(validRows.map(postJobPromise))
        .then(() => {
          //If any Invalid rows to show again
          setTableData(invalidRows);
          toast.success(
            `${validRows?.length} ${
              validRows?.length > 1 ? 'records' : 'record'
            } updated successfully.`,
            {
              theme: 'colored'
            }
          );
          setLoading(false);
          setValidateTable(false);
          setFiles([]);
          setEmptyFields('');
          navigate(-1);
        })
        .catch(error => {
          setLoading(false);
          console.error('error', error);
        });
    }
  };

  const animatedComponents = makeAnimated();

  let data = [
    {
      licensetype: ['LPN', 'CNA'],
      title: 'title',
      startdate: moment().format('MM-DD-YYYY'),
      enddate: moment().format('MM-DD-YYYY'),
      starttime: moment().format('h:mm A'),
      endtime: moment().format('h:mm A'),
      break: 'No Break',
      customervisibility: 'yes',
      notes: 'notes'
    }
  ];

  function convertToCSV(jsonData) {
    const separator = ',';
    const keys = Object.keys(jsonData[0]);
    const csvContent = [
      keys.join(separator),
      ...jsonData.map(item => {
        // Extract the 'FullAddress' and 'LicenseType' properties
        const { licensetype, ...rest } = item;

        // Join the values of the other properties with the 'FullAddress' and 'LicenseType' values
        const values = keys.map(key =>
          key === 'licensetype' ? `"${licensetype.join(', ')}"` : rest[key]
        );
        return values.join(separator);
      })
    ].join('\n');

    return csvContent;
  }

  const downloadCSVFile = data => {
    // Create a Blob object for the CSV data
    const blob = new Blob([data], { type: 'text/csv' });

    // Create a temporary URL for the Blob
    const url = URL.createObjectURL(blob);

    // Create a link element
    const link = document.createElement('a');
    link.href = url;
    link.download = `JobUploadTemplate.csv`;

    // Trigger the download
    link.click();

    // Clean up the temporary URL
    URL.revokeObjectURL(url);
  };

  return (
    <Card>
      <Card.Header>
        <Row className="flex-between-center">
          <Col sm={2}>
            <IconButton
              variant="falcon-default"
              size="sm"
              icon="download"
              transform="shrink-3"
              onClick={() => downloadCSVFile(convertToCSV(data))}
            >
              <span className="d-none d-sm-inline-block ms-1">Template</span>
            </IconButton>
          </Col>
        </Row>
      </Card.Header>
      <Card.Body style={{ marginTop: -15 }}>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Row className="mb-2">
            <Form.Group as={Col} md="4" className="mb-2" controlId="JobType">
              <Form.Label className="fs-9">Job Type</Form.Label>
              <Select
                className="basic-single"
                classNamePrefix="select"
                placeholder="Select..."
                isClearable
                isSearchable
                name="color"
                value={type}
                onChange={value => {
                  setType(value);
                }}
                options={[
                  {
                    value: 'Shift',
                    label: 'Shift'
                  },
                  {
                    value: 'Visit',
                    label: 'Visit'
                  }
                ]}
                components={animatedComponents}
              />
            </Form.Group>
            <Form.Group as={Col} md="4" className="mb-2" controlId="Manager">
              <Form.Label className="fs-9">Manager</Form.Label>
              <Select
                className="basic-single"
                classNamePrefix="select"
                placeholder="Select..."
                isClearable
                isSearchable
                name="color"
                value={valueManager}
                onChange={value => {
                  setValueManager(value);
                }}
                options={managers}
                components={animatedComponents}
                required
              />
            </Form.Group>
            <Form.Group as={Col} md="4" className="mb-2" controlId="Customer">
              <Form.Label className="fs-9">Customer</Form.Label>
              <Select
                className="basic-single"
                classNamePrefix="select"
                placeholder="Select..."
                isClearable
                isSearchable
                name="color"
                value={valueCustomer}
                onChange={value => {
                  setValueCustomer(value);
                }}
                options={customers}
                components={animatedComponents}
              />
            </Form.Group>
          </Row>
          {valueManager && valueCustomer && type ? (
            <>
              <div
                {...getRootProps({
                  className: `${classNames('dropzone-area py-6', {
                    'is-invalid': errors.uploadedFiles
                  })}`
                })}
              >
                <input name="uploadedFiles" {...getInputProps()} />
                <p className="fs-8 text-primary mb-1">{location}</p>
                <Flex justifyContent="center">
                  <img src={cloudUpload} alt="" width={25} className="me-2" />
                  <p className="fs-9 mb-0 text-700">
                    Drop your CSV or Excel files here
                  </p>
                </Flex>
                <p className="fs-12 mb-0 text-700 mt-5">
                  *FILE SHOULD INCLUDE ALL THE FIELDS
                </p>
                <div
                  style={{ display: 'flex', flexWrap: 'wrap' }}
                  className="mt-2 flex-1"
                >
                  {field.map(item => (
                    <Flex className="m-2 flex-1" key={item}>
                      <div>
                        <h6>{item}</h6>
                      </div>
                    </Flex>
                  ))}
                </div>
              </div>
              <div className="invalid-feedback">
                {errors.uploadedFiles?.message}
              </div>
              <div>
                {files.map(file => (
                  <Flex
                    alignItems="center"
                    className="py-3 border-bottom btn-reveal-trigger"
                    key={file.path}
                  >
                    <Flex
                      justifyContent="between"
                      alignItems="center"
                      className="ms-3 flex-1"
                    >
                      <div>
                        <h6>{file.path}</h6>
                        <Flex className="position-relative" alignItems="center">
                          <p className="mb-0 fs-10 text-400 line-height-1">
                            <strong>{getSize(file.size)}</strong>
                          </p>
                        </Flex>
                      </div>
                    </Flex>
                    <CardDropdown>
                      <div className="py-2">
                        <Dropdown.Item
                          className="text-danger"
                          onClick={() => handleRemove(file.path)}
                        >
                          Remove
                        </Dropdown.Item>
                      </div>
                    </CardDropdown>
                  </Flex>
                ))}
              </div>
              <Button
                variant="falcon-primary"
                type="submit"
                className="me-2 mt-3"
                size="sm"
              >
                Submit File
              </Button>
            </>
          ) : (
            <p className="mt-2">
              Select Manager , Customer and Type to continue...
            </p>
          )}
        </Form>

        {tableData.length !== 0 && (
          <AdvanceTableProvider {...table}>
            <Card className="mb-3 mt-5">
              <>
                <Card.Body className="p-0">
                  <AdvanceTable
                    headerClassName="bg-200 text-nowrap align-middle"
                    rowClassName="align-middle white-space-nowrap"
                    tableProps={{
                      size: 'sm',
                      striped: true,
                      className: 'fs-10 mb-0 overflow-hidden'
                    }}
                  />
                </Card.Body>
                <Card.Footer>
                  <div className="mt-3">
                    <AdvanceTableFooter
                      rowInfo
                      navButtons
                      rowsPerPageSelection
                    />
                  </div>
                  <Flex>
                    <Button
                      variant="primary"
                      className="me-2 mt-3"
                      size="sm"
                      onClick={() => validateFields()}
                    >
                      Validate
                    </Button>
                    {validateTable && (
                      <Button
                        variant="success"
                        className="me-2 mt-3"
                        size="sm"
                        onClick={() => (loading ? null : upload())}
                      >
                        {loading ? 'Loading...' : 'Upload File'}
                      </Button>
                    )}
                  </Flex>
                </Card.Footer>
              </>
            </Card>
          </AdvanceTableProvider>
        )}
      </Card.Body>
    </Card>
  );
}

export default ScheduleUpload;
